import React, { useState, useEffect } from "react";
import "./UserProfile.css";
import { Container, Row, Col, Form, Button, Modal } from "react-bootstrap";
import { createHostProfile, getHostProfileByHostId, getHostProfile, updateHostProfile, inviteHostUser, createHostRole, removeHostUser } from "../../../../api/host.api";
import Swal from "sweetalert2";

// -------------------- INITIAL STATES --------------------
const initialUser = {
  email: "",
  company: "",
  domeSerial: "",
  domeNickname: "",
  userName: "",
  role: "",
  password: "",
  pricePerSession: "",
};

const initialFeatures = {
  personalDevice: false,
  payment: false,
  booking: false,
};

const initialAccess = [
  { email: "email@example.com", status: "Access Granted" },
  { email: "email@example.com", status: "Invited" },
  { email: "email@example.com", status: "No Access" },
];

// -------------------- COMPONENT --------------------
const UserProfile = ({ isEditMode = false }) => {
  const storedHost = JSON.parse(localStorage.getItem("hostDetails"));
  const hostId = storedHost?.hostId || storedHost?._id;
  const hostMarket = storedHost?.market?.trim().toLowerCase() || 'market facing';
  const isMarketFacing = hostMarket === 'market facing';
  console.log("hostId from localStorage (calculated):", hostId);
  const [user, setUser] = useState(initialUser);
  const [features, setFeatures] = useState(initialFeatures);
  const [access, setAccess] = useState([initialAccess, initialAccess]);
  const [avatar, setAvatar] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("");
  const [roles, setRoles] = useState([]);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [allInvitedUsers, setAllInvitedUsers] = useState([]);
  const [visibleCount, setVisibleCount] = useState(6);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // -------------------- LOAD PROFILE --------------------
  useEffect(() => {
    if (hostId) loadHostProfile();
  }, [hostId]);

  useEffect(() => {
    if (allInvitedUsers?.length) {
      const sliced = allInvitedUsers.slice(0, visibleCount);
      const mid = Math.ceil(sliced.length / 2);
      setAccess([
        sliced.slice(0, mid),
        sliced.slice(mid),
      ]);
    } else {
      setAccess([[], []]);
    }
  }, [allInvitedUsers, visibleCount]);

  const loadHostProfile = async () => {
    try {
      setLoading(true);

      const profile = await getHostProfileByHostId(hostId);

      console.log("Loaded profile:", profile);

      if (!profile) {
        setError("Profile not found");
        return;
      }

      const isStaff = storedHost?.userType === "staff";

      setUser({
        email: isStaff ? storedHost.email : (profile.hostId?.primaryEmail || profile.email || ""),
        company: profile.hostId?.companyName || profile.company || "",
        domeSerial: profile.domeSerial || "",
        domeNickname: profile.domeNickname || "",
        userName: isStaff ? storedHost.email : (profile.hostId?.hostloginmail || profile.userName || ""),
        role: isStaff ? storedHost.role : (profile.role || ""),
        password: isStaff ? "********" : (profile.hostId?.password || ""),
        pricePerSession: profile.pricePerSession || "",
      });

      setFeatures({
        personalDevice: profile.features?.personalDevice ?? false,
        payment: profile.features?.payment ?? false,
        booking: profile.features?.booking ?? false,
      });

      setAvatar(profile.avatar || null);

      if (profile.invitedUsers) {
        setAllInvitedUsers(profile.invitedUsers);
      }

      if (profile.roles) {
        setRoles(profile.roles);
      }

    } catch (err) {
      setError(err.response?.data?.message || "Error loading profile");
    } finally {
      setLoading(false);
    }
  };



  // -------------------- HANDLE FORM INPUTS --------------------
  const handleInputChange = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handleFeatureChange = (e) => {
    const { name, checked } = e.target;
    setFeatures((prev) => {
      const updated = { ...prev, [name]: checked };
      // Logic: Synchronize payment and booking with personalDevice state
      if (name === "personalDevice") {
        updated.payment = checked;
        updated.booking = checked;
      }
      return updated;
    });
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          icon: 'warning',
          title: 'File Too Large',
          text: 'Avatar image must be smaller than 5MB.',
        });
        e.target.value = '';
        return;
      }
      setAvatar(URL.createObjectURL(file));
    }
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    if (!inviteEmail || !inviteRole) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Fields',
        text: 'Please enter both email and role',
      });
      return;
    }

    try {
      setLoading(true);
      await inviteHostUser({
        email: inviteEmail,
        role: inviteRole,
        hostId
      });
      Swal.fire({
        icon: 'success',
        title: 'Sent!',
        text: 'Invitation sent successfully!',
        timer: 2000,
        showConfirmButton: false
      });
      setInviteEmail("");
      setInviteRole("");
      loadHostProfile(); // Refresh list
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: err.response?.data?.message || "Failed to send invitation",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "This user will lose access immediately!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove them!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          await removeHostUser(userId);
          Swal.fire('Removed!', 'The user has been removed.', 'success');
          loadHostProfile(); // Refresh list
        } catch (err) {
          Swal.fire('Error', err.response?.data?.message || 'Failed to remove user', 'error');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const storedHost = JSON.parse(localStorage.getItem("hostDetails"));
      const localHostId = storedHost?._id;

      if (!localHostId) {
        Swal.fire({
          icon: 'error',
          title: 'Missing ID',
          text: 'Host ID missing',
        });
        return;
      }

      const form = new FormData();

      // User fields
      Object.keys(user).forEach((key) => {
        if (key !== "password" || user.password.trim() !== "") {
          form.append(key, user[key]);
        }
      });

      // Features
      form.append("features", JSON.stringify(features));

      // Avatar
      const fileInput = document.getElementById("avatar-upload");
      if (fileInput?.files[0]) {
        form.append("avatar", fileInput.files[0]);
      }

      await updateHostProfile(localHostId, form);
      Swal.fire({
        icon: 'success',
        title: 'Updated!',
        text: 'Profile Updated Successfully!',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: err.message || err.response?.data?.message || "Failed to update profile",
      });
    }
  };

  const handleCreateRole = async () => {
    if (!newRoleName) return;
    try {
      await createHostRole({ name: newRoleName, hostId });
      setNewRoleName("");
      setShowRoleModal(false);
      loadHostProfile(); // Refresh roles
      loadHostProfile(); // Refresh roles
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || "Failed to create role",
      });
    }
  };



  // -------------------- LOADING & ERROR UI --------------------
  if (loading)
    return (
      <Container className="py-5 text-center">Loading profile…</Container>
    );

  if (error)
    return (
      <Container className="py-5 text-center text-danger">
        {error}
        <Button onClick={loadHostProfile} className="ms-2">
          Retry
        </Button>
      </Container>
    );

  // -------------------- MAIN UI --------------------
  return (
    <Container fluid className="userprofile-bg py-5">
      <Row className="justify-content-center">
        {/* Avatar */}
        <Col xs={12} md={2} className="text-center mb-4">
          <div className="userprofile-avatar-wrapper">
            <img
              src={avatar || "/placeholder.svg"}
              alt="Avatar"
              className="userprofile-avatar"
            />
            <label htmlFor="avatar-upload" className="userprofile-avatar-edit">
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                hidden
              />
              <span className="userprofile-avatar-edit-icon">✎</span>
            </label>
          </div>
        </Col>

        {/* Form */}
        <Col xs={12} md={10}>
          <Form onSubmit={handleSubmit}>
            <Row className="g-3">

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    name="email"
                    value={user.email}
                    disabled={isEditMode}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Company Name</Form.Label>
                  <Form.Control
                    name="company"
                    value={user.company}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Dome Serial</Form.Label>
                  <Form.Control
                    name="domeSerial"
                    value={user.domeSerial}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>User Name</Form.Label>
                  <Form.Control
                    name="userName"
                    value={user.userName}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Role</Form.Label>
                  <Form.Control
                    name="role"
                    value={user.role}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Dome Nickname</Form.Label>
                  <Form.Control
                    name="domeNickname"
                    value={user.domeNickname}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>

              {!isEditMode && (
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                      name="password"
                      // type="password"
                      value={user.password}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
              )}

              {isEditMode && (
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Change Password (Optional)</Form.Label>
                    <Form.Control
                      name="password"
                      type="password"
                      value={user.password}
                      onChange={handleInputChange}
                      placeholder="Leave blank to keep old password"
                    />
                  </Form.Group>
                </Col>
              )}

              {isMarketFacing && (
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Price Per Session</Form.Label>
                    <Form.Control
                      type="number"
                      name="pricePerSession"
                      value={user.pricePerSession}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              )}

            </Row>
          </Form>
        </Col>
      </Row>

      {/* FEATURES */}
      {/* <hr />
      <div className="text-center userprofile-section-title">DOME FEATURES</div>

      <Row className="justify-content-center mt-3">
        <Col md={8} className="d-flex gap-4 justify-content-center flex-wrap">

          <div className="userprofile-checkbox-group">
            <label>Allow Personal Device Drive</label>
            <input
              type="checkbox"
              name="personalDevice"
              checked={features.personalDevice}
              onChange={handleFeatureChange}
            />
          </div>

          <div className="userprofile-checkbox-group">
            <label>Allow Somadome Payment</label>
            <input
              type="checkbox"
              name="payment"
              checked={features.payment}
              onChange={handleFeatureChange}
            />
          </div>

          <div className="userprofile-checkbox-group">
            <label>Allow Somadome Booking</label>
            <input
              type="checkbox"
              name="booking"
              checked={features.booking}
              onChange={handleFeatureChange}
            />
          </div>

        </Col>
      </Row> */}

      {/* ACCESS */}
      <hr className="mt-4" />
      <div className="text-center userprofile-section-title mb-4">USER ACCESS</div>

      <Row className="justify-content-center">

        {/* Column 1 */}
        <Col md={3}>
          {access[0].map((usr, idx) => (
            <div className="userprofile-access-row" key={idx}>
              <span className="email" title={usr.email}>{usr.email}</span>
              <div className="d-flex align-items-center">
                <span
                  className={`userprofile-access-status userprofile-access-status-${usr.status?.replace(
                    /\s/g,
                    ""
                  ).toLowerCase()}`}
                >
                  {usr.status}
                </span>
                {usr._id && (
                  <i
                    className="fas fa-trash text-danger ms-2"
                    style={{ cursor: "pointer", fontSize: "0.9rem" }}
                    onClick={() => handleRemoveUser(usr._id)}
                    title="Remove User"
                  ></i>
                )}
              </div>
            </div>
          ))}
        </Col>

        {/* Column 2 */}
        <Col md={3}>
          {access[1].map((usr, idx) => (
            <div className="userprofile-access-row" key={idx}>
              <span className="email" title={usr.email}>{usr.email}</span>
              <div className="d-flex align-items-center">
                <span
                  className={`userprofile-access-status userprofile-access-status-${usr.status?.replace(
                    /\s/g,
                    ""
                  ).toLowerCase()}`}
                >
                  {usr.status}
                </span>
                {usr._id && (
                  <i
                    className="fas fa-trash text-danger ms-2"
                    style={{ cursor: "pointer", fontSize: "0.9rem" }}
                    onClick={() => handleRemoveUser(usr._id)}
                    title="Remove User"
                  ></i>
                )}
              </div>
            </div>
          ))}
          <div className="d-flex justify-content-center gap-2 mt-2">
            {allInvitedUsers.length > visibleCount && (
              <Button
                variant="link"
                className="userprofile-load-more-btn"
                onClick={() => setVisibleCount(prev => prev + 6)}
              >
                Load More...
              </Button>
            )}
            {visibleCount > 6 && (
              <Button
                variant="link"
                className="userprofile-load-more-btn text-danger"
                onClick={() => setVisibleCount(6)}
              >
                Show Less
              </Button>
            )}
          </div>
        </Col>

        {/* Invite */}
        <Col md={3}>
          <Form.Group className="mb-2">
            <Form.Label>Invite User Email</Form.Label>
            <Form.Control
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="d-flex justify-content-between align-items-center">
              Role
              <span
                className="userprofile-add-role-icon"
                onClick={() => setShowRoleModal(true)}
                title="Create New Role"
                style={{ cursor: 'pointer', fontSize: '1.2rem', color: '#007bff' }}
              >
                +
              </span>
            </Form.Label>
            <Form.Select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
            >
              <option value="">Select Role</option>
              {roles.map((r) => (
                <option key={r._id} value={r.name}>{r.name}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Button
            className="userprofile-submit-btn w-100"
            onClick={handleInviteSubmit}
          >
            SUBMIT
          </Button>

          <div className="mt-4">
            <Button
              className="userprofile-update-btn w-100"
              variant="outline-primary"
              onClick={handleSubmit}
            >
              UPDATE PROFILE
            </Button>
          </div>
        </Col>
      </Row>

      {/* Role Creation Modal */}
      <Modal show={showRoleModal} onHide={() => setShowRoleModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Role Name</Form.Label>
            <Form.Control
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              placeholder="e.g. Manager, Staff"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRoleModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateRole}>
            Create Role
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UserProfile;
